import { doc, updateDoc } from "firebase/firestore";
import { db } from "./firebase";

// Photon (OpenStreetMap) geocoder: free and its results may be stored, unlike Google's.
const PHOTON_URL = "https://photon.komoot.io/api/";

const hasCoords = (place) => Number.isFinite(place.lat) && Number.isFinite(place.lng);

const photonLookup = async (query) => {
  const params = new URLSearchParams({ q: query, limit: "1", lang: "default" });
  const res = await fetch(`${PHOTON_URL}?${params}`);
  if (!res.ok) throw new Error(`Photon ${res.status}`);
  const [feature] = (await res.json()).features;
  if (!feature) return null;
  const [lng, lat] = feature.geometry.coordinates;
  return { lat, lng };
};

/** Coordinates for a publication's address, falling back to just its city if the street isn't found. */
export const geocodePlace = async ({ address, city, province, country }) => {
  const area = [city, province, country].filter(Boolean).join(", ");
  if (!area) return null;
  return (address && (await photonLookup(`${address}, ${area}`))) || photonLookup(area);
};

/** Geocodes a publication and stores lat/lng on its Firestore doc, so it only happens once. */
export const locatePlace = async (place) => {
  const coords = await geocodePlace(place);
  if (coords) {
    // Still usable on the map this session even if saving fails.
    updateDoc(doc(db, "places", place.id), coords).catch((error) =>
      console.error(`No se pudo guardar la ubicación de ${place.id}:`, error)
    );
  }
  return coords;
};

// Tried this session (found or not), so a missing address isn't retried on every update.
const attempted = new Set();

/**
 * Backfills coordinates for older publications that were saved without them.
 * Runs a couple at a time; `onLocated(id, coords)` fires as each one resolves.
 */
export const locateMissingPlaces = async (places, onLocated) => {
  const queue = places.filter((p) => !hasCoords(p) && !attempted.has(p.id));
  queue.forEach((p) => attempted.add(p.id));

  const worker = async () => {
    while (queue.length) {
      const place = queue.shift();
      try {
        const coords = await locatePlace(place);
        if (coords) onLocated(place.id, coords);
      } catch (error) {
        console.error(`No se pudo ubicar la publicación ${place.id}:`, error);
      }
    }
  };
  await Promise.all([worker(), worker()]);
};

export { hasCoords };
